import { useMemo, useState } from "react";
import {
  BookOpen,
  BrainCircuit,
  Clock3,
  FlaskConical,
  HeartPulse,
  History,
  Search,
  ShieldAlert,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LibraryEntityDetail } from "@/components/library/LibraryEntityDetail";
import { useSubstances } from "@/lib/usePatients";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { normalize } from "@/lib/pharmacology/aliasResolver";
import {
  categoryCount,
  getLibraryFavorites,
  getLibraryRecents,
  LIBRARY_CATEGORIES,
  libraryKey,
  rememberLibraryRecent,
  searchLibrary,
  setLibraryFavorite,
  templateAliases,
  templateCategories,
  type LibraryCategoryKey,
} from "@/lib/library/libraryIndex";
import type { KBTemplate } from "@/lib/pharmacologyKnowledgeBase";

function categoryStyle(key: LibraryCategoryKey) {
  const styles: Partial<Record<LibraryCategoryKey, string>> = {
    medicamentos: "from-primary/25 via-primary/10 to-card",
    comuns: "from-cyan-500/20 via-cyan-500/5 to-card",
    estimulantes: "from-orange-500/20 via-orange-500/5 to-card",
    depressores: "from-sky-500/20 via-sky-500/5 to-card",
    opioides: "from-rose-500/20 via-rose-500/5 to-card",
    benzodiazepinicos: "from-blue-500/20 via-blue-500/5 to-card",
    antidepressivos: "from-violet-500/20 via-violet-500/5 to-card",
    antipsicoticos: "from-fuchsia-500/20 via-fuchsia-500/5 to-card",
    estabilizadores: "from-emerald-500/20 via-emerald-500/5 to-card",
    tdah: "from-amber-500/20 via-amber-500/5 to-card",
    dissociativos: "from-teal-500/20 via-teal-500/5 to-card",
    psicodelicos: "from-purple-500/20 via-purple-500/5 to-card",
    empatogenos: "from-pink-500/20 via-pink-500/5 to-card",
    canabinoides: "from-lime-500/20 via-lime-500/5 to-card",
    alcool: "from-yellow-500/20 via-yellow-500/5 to-card",
    outros: "from-muted/60 via-muted/20 to-card",
  };
  return styles[key] ?? styles.outros!;
}

function EntityRow({
  template,
  favorite,
  clinical,
  onOpen,
  onToggleFavorite,
}: {
  template: KBTemplate;
  favorite: boolean;
  clinical: boolean;
  onOpen: () => void;
  onToggleFavorite: () => void;
}) {
  const aliases = templateAliases(template);
  const categories = templateCategories(template);
  return (
    <Card className="overflow-hidden rounded-3xl border-border/80 bg-card/90 shadow-sm">
      <button type="button" onClick={onOpen} className="w-full p-4 text-left sm:p-5">
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border bg-muted/40 text-primary">
            <FlaskConical className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="break-words font-semibold leading-tight">{template.name}</h3>
              {clinical ? <Badge variant="default" className="text-[9px]">base clínica</Badge> : null}
              {template.needs_review || /revis/i.test(String(template.review_status ?? "")) ? <Badge variant="outline" className="text-[9px]">em revisão</Badge> : null}
            </div>
            <div className="mt-1 break-words text-xs text-muted-foreground">
              {template.pharmacological_class ?? template.clinical_category ?? template.substance_type ?? "Registro farmacológico"}
            </div>
            {aliases.length ? <div className="mt-1 line-clamp-1 text-[11px] text-muted-foreground">{aliases.slice(0, 5).join(" · ")}</div> : null}
            <div className="mt-2 flex flex-wrap gap-1">
              {categories.slice(0, 3).map((category) => <Badge key={category} variant="secondary" className="text-[9px]">{LIBRARY_CATEGORIES.find((item) => item.key === category)?.label ?? category}</Badge>)}
              {template.evidence_level ? <Badge variant="outline" className="text-[9px] max-w-full truncate">{template.evidence_level}</Badge> : null}
            </div>
          </div>
        </div>
      </button>
      <div className="flex items-center justify-between border-t px-4 py-2.5 sm:px-5">
        <div className="text-[10px] text-muted-foreground">{template.default_route ? `via principal: ${template.default_route}` : "via não informada"}</div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-xl"
          onClick={onToggleFavorite}
          aria-label={favorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        >
          <Star className={`h-4 w-4 ${favorite ? "fill-current text-primary" : ""}`} />
        </Button>
      </div>
    </Card>
  );
}

export default function Library() {
  const knowledge = useMemo(() => getRuntimeKnowledgeBase(), []);
  const { data: clinicalSubstances = [] } = useSubstances();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<LibraryCategoryKey | "all">("all");
  const [selected, setSelected] = useState<KBTemplate | null>(null);
  const [favorites, setFavorites] = useState<string[]>(() => getLibraryFavorites());
  const [recents, setRecents] = useState<string[]>(() => getLibraryRecents());

  const clinicalKeys = useMemo(
    () => new Set((clinicalSubstances as any[]).map((item) => normalize(item.name ?? "")).filter(Boolean)),
    [clinicalSubstances],
  );

  const filtered = useMemo(() => {
    const initial = query.trim() ? searchLibrary(knowledge, query, 180) : knowledge;
    if (category === "all") return initial;
    return initial.filter((template) => templateCategories(template).includes(category));
  }, [knowledge, query, category]);

  const favoriteTemplates = useMemo(
    () => favorites.map((key) => knowledge.find((template) => libraryKey(template) === key)).filter(Boolean) as KBTemplate[],
    [favorites, knowledge],
  );
  const recentTemplates = useMemo(
    () => recents.map((key) => knowledge.find((template) => libraryKey(template) === key)).filter(Boolean).slice(0, 8) as KBTemplate[],
    [recents, knowledge],
  );

  function openTemplate(template: KBTemplate) {
    setSelected(template);
    setRecents(rememberLibraryRecent(libraryKey(template)));
  }

  function toggleFavorite(template: KBTemplate) {
    const key = libraryKey(template);
    setFavorites(setLibraryFavorite(key, !favorites.includes(key)));
  }

  const visible = filtered.slice(0, 180);

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 pb-4 sm:space-y-6">
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">ClinicalOS Knowledge</div>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">Biblioteca</h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              Substâncias, medicamentos, aliases, farmacologia, PK/PD, segurança e evidência em uma única base estruturada. Conhecimento canônico permanece separado do prontuário individual.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline"><a href="/interacoes"><Zap className="mr-2 h-4 w-4" />Interações</a></Button>
            <Button asChild variant="outline"><a href="/conhecimento-farmacologico"><BookOpen className="mr-2 h-4 w-4" />Manutenção</a></Button>
          </div>
        </div>

        <Card className="sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-20 rounded-3xl border-primary/15 bg-card/95 p-3 shadow-sm backdrop-blur-xl sm:p-4 md:static">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar substância, medicamento, marca, alias ou classe…"
              className="h-12 rounded-2xl pl-11 text-base"
              autoComplete="off"
              enterKeyHint="search"
            />
          </div>
          {query.trim() ? (
            <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{filtered.length} resultado(s)</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setQuery("")}>Limpar</Button>
            </div>
          ) : null}
        </Card>
      </section>

      {!query.trim() && (
        <>
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h2 className="font-semibold">Sua biblioteca</h2>
                <p className="text-xs text-muted-foreground">Favoritos e registros consultados recentemente ficam apenas neste navegador.</p>
              </div>
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Card className="rounded-3xl p-4"><div className="flex items-center gap-2 text-sm font-medium"><Star className="h-4 w-4 text-primary" />Favoritos</div><div className="mt-3 text-3xl font-semibold">{favoriteTemplates.length}</div><div className="text-xs text-muted-foreground">itens salvos</div></Card>
              <Card className="rounded-3xl p-4"><div className="flex items-center gap-2 text-sm font-medium"><History className="h-4 w-4 text-primary" />Recentes</div><div className="mt-3 text-3xl font-semibold">{recentTemplates.length}</div><div className="text-xs text-muted-foreground">consultados</div></Card>
              <Card className="rounded-3xl p-4"><div className="flex items-center gap-2 text-sm font-medium"><FlaskConical className="h-4 w-4 text-primary" />Conhecimento</div><div className="mt-3 text-3xl font-semibold">{knowledge.length}</div><div className="text-xs text-muted-foreground">entidades estruturadas</div></Card>
              <Card className="rounded-3xl p-4"><div className="flex items-center gap-2 text-sm font-medium"><HeartPulse className="h-4 w-4 text-primary" />Base clínica</div><div className="mt-3 text-3xl font-semibold">{clinicalSubstances.length}</div><div className="text-xs text-muted-foreground">canônicos no Supabase</div></Card>
            </div>
          </section>

          {favoriteTemplates.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-semibold">Favoritos</h2>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {favoriteTemplates.slice(0, 6).map((template) => (
                  <EntityRow
                    key={libraryKey(template)}
                    template={template}
                    favorite
                    clinical={clinicalKeys.has(normalize(template.name))}
                    onOpen={() => openTemplate(template)}
                    onToggleFavorite={() => toggleFavorite(template)}
                  />
                ))}
              </div>
            </section>
          )}

          {recentTemplates.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-semibold">Consultados recentemente</h2>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {recentTemplates.map((template) => (
                  <button key={libraryKey(template)} type="button" onClick={() => openTemplate(template)} className="rounded-2xl border bg-card p-3 text-left transition-colors hover:bg-muted/30">
                    <div className="font-medium">{template.name}</div>
                    <div className="mt-1 line-clamp-1 text-xs text-muted-foreground">{template.pharmacological_class ?? template.clinical_category ?? "registro farmacológico"}</div>
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="space-y-3">
            <div>
              <h2 className="font-semibold">Explorar por categoria</h2>
              <p className="text-xs text-muted-foreground">Categorias são derivadas dos dados farmacológicos, não listas visuais isoladas.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-5">
              {LIBRARY_CATEGORIES.filter((item) => item.key !== "outros").map((item) => {
                const count = categoryCount(knowledge, item.key);
                if (!count) return null;
                return (
                  <button
                    type="button"
                    key={item.key}
                    onClick={() => setCategory(item.key)}
                    className={`min-h-36 rounded-3xl border bg-gradient-to-br ${categoryStyle(item.key)} p-4 text-left shadow-sm transition-transform active:scale-[0.98]`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-lg font-semibold leading-tight">{item.label}</div>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                    <div className="mt-3 text-xs leading-relaxed text-muted-foreground">{item.description}</div>
                  </button>
                );
              })}
            </div>
          </section>
        </>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold">{query.trim() ? "Resultados" : category === "all" ? "Toda a biblioteca" : LIBRARY_CATEGORIES.find((item) => item.key === category)?.label}</h2>
            <p className="text-xs text-muted-foreground">{filtered.length} item(ns) encontrados. Conteúdo em revisão permanece identificado.</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button type="button" size="sm" variant={category === "all" ? "default" : "outline"} onClick={() => setCategory("all")}>Todos</Button>
            {category !== "all" ? <Button type="button" size="sm" variant="outline" onClick={() => setCategory("all")}>Limpar categoria</Button> : null}
          </div>
        </div>

        {visible.length ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visible.map((template) => {
              const key = libraryKey(template);
              return (
                <EntityRow
                  key={key}
                  template={template}
                  favorite={favorites.includes(key)}
                  clinical={clinicalKeys.has(normalize(template.name))}
                  onOpen={() => openTemplate(template)}
                  onToggleFavorite={() => toggleFavorite(template)}
                />
              );
            })}
          </div>
        ) : (
          <Card className="rounded-3xl p-8 text-center">
            <ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground" />
            <div className="mt-3 font-medium">Nenhum registro encontrado</div>
            <div className="mt-1 text-sm text-muted-foreground">Tente outro nome, marca, alias ou classe. Ausência no catálogo não significa ausência de risco.</div>
          </Card>
        )}
        {filtered.length > visible.length ? <div className="text-center text-xs text-muted-foreground">Mostrando os primeiros {visible.length} de {filtered.length} registros para manter a interface rápida.</div> : null}
      </section>

      <Card className="rounded-3xl border-primary/15 bg-primary/5 p-4 sm:p-5">
        <div className="flex gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary"><BrainCircuit className="h-5 w-5" /></div>
          <div>
            <div className="font-medium">Uma base, vários contextos</div>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">A Biblioteca é conhecimento canônico. Journal, PK/PD, Interações e Clínica usam essa mesma estrutura, enquanto resposta individual, doses e prontuário permanecem dados separados.</p>
          </div>
        </div>
      </Card>

      <LibraryEntityDetail
        template={selected}
        open={!!selected}
        onOpenChange={(open) => !open && setSelected(null)}
        favorite={!!selected && favorites.includes(libraryKey(selected))}
        onToggleFavorite={() => selected && toggleFavorite(selected)}
      />
    </div>
  );
}
